import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useLazyGetDealerRolesFirmByIDQuery } from "../../store/dealerRoles/firmApi";
import useAuth from "../useAuth";
import usePermission from "../usePermission";
import { getTabsForFirm, getFirmIcon, getFirmTypeBadge, normalizeFirmTabId } from "../../utils/firmUtils";

export default function useFirmDetail() {
  const { firmId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();

  const [firm, setFirm] = useState(location.state?.firm || null);
  const [selectedTab, setSelectedTab] = useState(normalizeFirmTabId(location.state?.toPageTab) || "info");
  const [alerts, setAlerts] = useState([]);

  const isOwnDealerFirm = Boolean(
    firm?._id && auth?.firm && String(auth.firm) === String(firm._id)
  );

  const [getFirmByID, { data: firmData, isLoading, isFetching }] =
    useLazyGetDealerRolesFirmByIDQuery();

  const fetchFirm = useCallback(() => {
    if (auth?.accessToken && firmId) {
      getFirmByID({ token: auth.accessToken, id: firmId });
    }
  }, [auth?.accessToken, firmId, getFirmByID]);

  useEffect(() => {
    fetchFirm();
  }, [fetchFirm]);

  useEffect(() => {
    if (firmData?.data?.firm) {
      const fetched = firmData.data.firm;
      const preserved = location.state?.firm?.dealerRelation;
      const firmToSet = preserved ? { ...fetched, dealerRelation: preserved } : fetched;
      setFirm(firmToSet);
    }
  }, [firmData, location.state?.firm?.dealerRelation]);

  const addAlert = (message, severity, autoRemoveMs = 3000) => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    }, autoRemoveMs);
  };

  const dismissAlert = (id) => setAlerts((prev) => prev.filter((a) => a.id !== id));

  const handleFirmUpdate = (newFirm) => {
    setFirm(newFirm);
  };

  const handleGoBack = () => {
    if (location.state?.fromPage) {
      navigate(-1);
    } else {
      navigate("/Anasayfa/Firma");
    }
  };

  const handleNavigateToFirm = (selectedFirm) => {
    navigate(`/Anasayfa/Firma/${selectedFirm._id}`, {
      state: { firm: selectedFirm, fromPage: "Firma" },
    });
  };

  const { hasPermission, hasAnyPermission } = usePermission();
  const tabs = useMemo(() => {
    if (!firm) return [];
    return getTabsForFirm(firm, { role: "dealer" }).filter((tab) => {
      if (!tab.permission) return true;
      if (Array.isArray(tab.permission)) return hasAnyPermission(...tab.permission);
      return hasPermission(tab.permission);
    });
  }, [firm, hasPermission, hasAnyPermission]);

  // Firma veya tab listesi değiştiğinde, selectedTab izinli değilse ilk izinli sekmeye geç
  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((t) => t.id === selectedTab)) {
      setSelectedTab(tabs[0].id);
    }
  }, [tabs, selectedTab]);

  const firmIcon = firm ? getFirmIcon(firm) : null;
  const firmTypeBadge = firm ? getFirmTypeBadge(firm) : null;

  return {
    firm,
    isOwnDealerFirm,
    isFetching,
    isLoading,
    selectedTab,
    setSelectedTab,
    tabs,
    firmTypeBadge,
    firmIcon,
    alerts,
    addAlert,
    dismissAlert,
    handleFirmUpdate,
    handleGoBack,
    handleNavigateToFirm,
  };
}
